'use client';

import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ComponentType,
  ElementType,
  ReactNode,
} from 'react';
import {
  useMemo,
} from 'react';

import classNames from 'classnames';

import useUniqueId from '@uikit/utils/hooks/useUniqueId';

import { createSafeContext, useSafeContext } from '@utilities/context';

export type FormControlLabelProps = ComponentPropsWithoutRef<'label'>;

export const FormControlLabel = ({ id, className, children, ...props }: FormControlLabelProps) => {
  const {
    id: formControlId,
    isError,
    isSuccess,
    disabled,
    required,
    visuallyRequired,
  } = useFormControl();
  const actualId = formControlId || id;

  return (
    <label
      className={classNames(
        'form-control-label',
        {
          'form-control-label-error': isError,
          'form-control-label-success': isSuccess,
          'form-control-label-disabled': disabled,
        },
        className,
      )}
      htmlFor={actualId}
      {...props}
    >
      {(required || visuallyRequired) && (
        <span aria-hidden="true" className="form-control-label-required">
          *&nbsp;
        </span>
      )}
      {children}
    </label>
  );
};

type FormControlHelperTextProps = ComponentPropsWithoutRef<'p'>;

const FormControlHelperText = ({ className, ...props }: FormControlHelperTextProps) => {
  const { isError, isSuccess, disabled } = useFormControl();

  return (
    <p
      className={classNames(
        'form-control-helper-text',
        {
          'form-control-helper-text-error': isError,
          'form-control-helper-text-success': isSuccess,
          'form-control-helper-text-disabled': disabled,
        },
        className,
      )}
      role={isError ? 'alert' : ''}
      {...props}
    />
  );
};

export interface ContextValue {
  id: string;
  isError?: boolean;
  isSuccess?: boolean;
  disabled?: boolean;
  required?: boolean;
  visuallyRequired?: boolean;
}

const FormControlContext = createSafeContext<ContextValue>();

export const useFormControl = () => useSafeContext(FormControlContext);

export type BaseFormControlFieldProps = Partial<ContextValue>;

export type FormControlProps<
  FieldComponent extends ComponentType<BaseFormControlFieldProps>,
  FieldProps extends ComponentPropsWithRef<ElementType> = ComponentPropsWithRef<FieldComponent>,
> = {
  id?: string;
  helperText?: ReactNode;
  label?: ReactNode;
  children?: ReactNode;
  required?: boolean;
  visuallyRequired?: boolean;
  labelClassName?: string;
  Field?: ComponentType<FieldProps>;
} & FieldProps &
  BaseFormControlFieldProps;

export const FormControl = <
  FieldComponent extends ComponentType<BaseFormControlFieldProps>,
  FieldProps extends ComponentPropsWithRef<ElementType> = ComponentPropsWithRef<FieldComponent>,
>(
  {
    id,
    label,
    helperText,
    isError,
    isSuccess,
    disabled,
    Field,
    required,
    visuallyRequired,
    labelClassName,
    ...props
  }: FormControlProps<FieldComponent, FieldProps>,
) => {
  const fieldId = useUniqueId(id);
  const value = useMemo(() => {
    return {
      id: fieldId,
      isError,
      isSuccess,
      disabled,
      required,
      visuallyRequired,
    };
  }, [fieldId, isError, visuallyRequired, required, isSuccess, disabled]);

  return (
    <FormControlContext.Provider value={value}>
      {label && <FormControlLabel className={labelClassName}>{label}</FormControlLabel>}
      {Field ? (
        <Field
          // ref={props.ref}
          {...(props as any)}
          isError={isError}
          isSuccess={isSuccess}
          disabled={disabled}
          required={required}
          id={fieldId}
        />
      ) : (
        props.children
      )}
      {helperText && <FormControlHelperText>{helperText}</FormControlHelperText>}
    </FormControlContext.Provider>
  );
};

export default FormControl;
